<template>
  <div class="border rounded-lg overflow-hidden text-textPrimary">
    <!-- Rolagem horizontal própria: nenhuma coluna (ex.: VALOR) é cortada. -->
    <div class="overflow-x-auto" data-testid="management-doctor-table-scroll">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              v-for="header in headers"
              :key="header.key"
              class="px-3 py-2 text-center text-xs font-bold text-textPrimary uppercase tracking-wider"
            >
              {{ header.label }}
            </th>
          </tr>
        </thead>

        <tbody class="bg-white divide-y divide-gray-200 text-sm">
          <tr v-if="!items || items.length === 0">
            <td :colspan="headers.length" class="text-center py-4 text-gray-500">
              Nenhum plantão para exibir.
            </td>
          </tr>

          <tr v-for="(item, index) in items" :key="index">
            <td
              v-for="header in headers"
              :key="header.key"
              :class="['px-3 py-2 text-center', cellClass(header.key)]"
            >
              <template v-if="header.key === 'availability'">
                <span
                  :class="[
                    'px-2 py-1 rounded-md border text-xs',
                    getAvailabilityClass(item.availability)
                  ]"
                >
                  <v-icon
                    v-if="item.availability === 'Conflitante'"
                    :icon="mdiAlertCircleOutline"
                    :color="'white'"
                    class="w-5 h-5 shrink-0"
                  />
                  <span class="leading-none">{{ item[header.key] }}</span>
                </span>
              </template>

              <template v-else-if="header.key === 'action'">
                <span
                  :class="['action-chip', getActionClass(item.actionTone)]"
                  :data-testid="`preview-action-${index}`"
                  ><svg
                    v-if="item.actionTone === 'blocked'"
                    class="action-chip-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    data-testid="preview-lock-icon"
                  >
                    <path :d="mdiLock" /></svg
                  >{{ item.actionLabel }}</span
                >
                <!-- Motivo do bloqueio (ex.: "Antecipe solicitado ou pago") quebra linha, sem corte. -->
                <span v-if="item.actionNote" class="mt-1 block text-xs text-gray-500">{{
                  item.actionNote
                }}</span>
              </template>

              <template v-else> {{ item[header.key] }} </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Fora das células: o botão não cobre mais a última coluna. -->
    <div
      v-if="expandable"
      class="flex justify-end border-t border-gray-200 bg-gray-50 px-2 py-1"
      data-testid="management-doctor-table-expand-bar"
    >
      <button
        type="button"
        class="flex items-center gap-2 rounded-full px-2 py-1 text-xs font-bold text-primary30 hover:bg-gray-100 transition-colors"
        title="Expandir tabela"
        data-testid="management-doctor-table-expand"
        @click="$emit('expand')"
      >
        <span
          class="w-6 h-6 bg-white border-2 border-primary30 rounded-full flex items-center justify-center"
        >
          <ExpandIcon class="w-[10px] h-[10px]" />
        </span>
        Expandir tabela
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ExpandIcon from '@/assets/icons/expand-icon.vue'
import { mdiAlertCircleOutline, mdiLock } from '@mdi/js'
import { PropType } from 'vue'

interface TableHeader {
  key: string
  label: string
}

defineProps({
  headers: {
    type: Array as PropType<TableHeader[]>,
    required: true
  },
  items: {
    type: Array as PropType<any[]>,
    required: true
  },

  expandable: {
    type: Boolean,
    default: false
  }
})

defineEmits(['expand'])

// Colunas de texto livre quebram linha; as demais ficam numa linha só (sem corte: o
// wrapper rola na horizontal se ainda assim não couber).
const WRAPPING_CELLS: Record<string, string> = {
  action: 'min-w-[140px] whitespace-normal',
  current: 'min-w-[120px] whitespace-normal'
}
const cellClass = (key: string) => WRAPPING_CELLS[key] ?? 'whitespace-nowrap'

const getAvailabilityClass = (availability: string) => {
  if (availability === 'Conflitante') {
    return 'text-white bg-[#DA1E28] border-[#DA1E28] hover:bg-[#C11B23] active:bg-[#A7171E] inline-flex items-center gap-1'
  }
  if (availability === 'Aberta')
    return 'bg-[var(--st-open-bg,#ffedd5)] border-[color:var(--st-open-stripe,#fb923c)]'
  if (availability === 'Com médico') return 'bg-[#E0F2F6] border-primary10'
  if (availability === 'Ocupado s/ ident.') return 'availability-unidentified border-neutralLight'
  return 'bg-gray-100 border-gray-400'
}

// Chip da ação na cor do estado de DESTINO (mesma paleta dos chips do resumo).
const ACTION_CLASSES: Record<string, string> = {
  doctor: 'action-chip-doctor',
  unidentified: 'action-chip-unidentified',
  open: 'action-chip-open',
  blocked: 'action-chip-blocked',
  none: 'action-chip-none'
}
const getActionClass = (tone: string | undefined) =>
  ACTION_CLASSES[tone ?? 'none'] ?? ACTION_CLASSES.none
</script>

<style scoped>
.availability-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
}
.action-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  border: 1px solid;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
}
.action-chip-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  fill: currentColor;
}
.action-chip-doctor {
  background: #e0f2f6;
  border-color: #84c8d7;
  color: #235e6c;
}
.action-chip-unidentified {
  background: repeating-linear-gradient(135deg, #eeeef3 0 5px, #e1e1ea 5px 10px);
  border-color: #bcbccd;
  color: #3e3e5a;
}
.action-chip-open {
  background: var(--st-open-bg, #fef3e7);
  border-color: var(--st-open-stripe, #f18f1b);
  color: var(--st-open-text, #8a4b00);
}
.action-chip-blocked {
  background: #fdecec;
  border-color: #f3a5a9;
  color: #da1e28;
}
.action-chip-none {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #6b7280;
}
</style>
